/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react/prop-types */
import CircularProgress from "@mui/material/CircularProgress";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  getDescriptionKey,
  getTagKey,
  getTimeStampKey,
  getValueKey,
  hasInvalidCubePayload,
} from "../../../Utils/UtilFunctions/cubeUtil";
import { useEffect, memo, useContext, useMemo } from "react";
import LineChart from "../Silos/components/LineChart/linechart";
import { getFullRangeCategories } from "../../../Utils/util";
import useTrackedCubeQuery from "../../../Utils/UtilFunctions/useTrackedCubeQuery";
import { AppContext } from "../../../App";
import { Box, Typography } from "@mui/material";
import SubtitlesOffOutlinedIcon from "@mui/icons-material/SubtitlesOffOutlined";

interface LineChartCubeResponseProps {
  tag: any;
  limit?: number;
  single?: boolean;
  type?: string;
  dateRange?: string;
  granularity?: any;
  measureType?: any;
  isExtended?: boolean;
  width?: any;
  height?: any;
  setExcelData?: any;
  fetchedAnnotations?: any;
  unit?: any;
  limits?: any;
}

const LineChartCubeResponse: React.FC<LineChartCubeResponseProps> = ({
  tag,
  limit,
  single,
  type,
  width,
  height,
  dateRange,
  granularity,
  measureType,
  isExtended,
  unit,
  setExcelData = () => {},
  fetchedAnnotations,
  limits,
}) => {
  const appContextProps: any = useContext(AppContext);
  const result: any = [];
  const query: any = {
    measures: [
      `${appContextProps?.selectedPlantForCube}` + measureType + ".lastValue",
    ],
    dimensions: [
      `${appContextProps?.selectedPlantForCube}` + measureType + ".tagName",
      `${appContextProps?.selectedPlantForCube}` + measureType + ".description",
    ],
    filters: [
      {
        member:
          `${appContextProps?.selectedPlantForCube}` + measureType + ".tagName",
        operator: "equals",
        values: Array.isArray(tag) ? tag : [tag],
      },
    ],
    timeDimensions: [
      {
        dimension:
          `${appContextProps?.selectedPlantForCube}` +
          measureType +
          ".timestamp",
        granularity: granularity,
        dateRange: dateRange,
      },
    ],
    order: [
      [
        `${appContextProps?.selectedPlantForCube}` + measureType + ".timestamp",
        "desc",
      ],
    ],
    timezone: "America/Los_Angeles",
    // limit: single === true ? limit : 100,
  };
  const { resultSet, isLoading, error, progress }: any = useTrackedCubeQuery(
    query,
    {
      skip: hasInvalidCubePayload(query),
    }
  );
const limitsCubeQuery: any = {
    measures: [
      `${appContextProps?.selectedPlantForCube}` +
        "ActionableFiveMinAgg" +
        ".lastValue",
    ],
    dimensions: [
      `${appContextProps?.selectedPlantForCube}` +
        "ActionableFiveMinAgg" +
        ".tagName",
      `${appContextProps?.selectedPlantForCube}` +
        "ActionableFiveMinAgg" +
        ".description",
    ],
    filters: [
      {
        member:
          `${appContextProps?.selectedPlantForCube}` +
          "ActionableFiveMinAgg" +
          ".tagName",
        operator: "equals",
        values: Array.isArray(tag) ? [] : [tag + "_LL", tag + "_UL"],
      },
    ],
    timeDimensions: [
      {
        dimension:
          `${appContextProps?.selectedPlantForCube}` +
          "ActionableFiveMinAgg" +
          ".timestamp",
        granularity: "minute",
        dateRange: dateRange,
      },
    ],
    order: [
      [
        `${appContextProps?.selectedPlantForCube}` +
          "ActionableFiveMinAgg" +
          ".timestamp",
        "desc",
      ],
    ],
    timezone: "America/Los_Angeles",
    limit: 2,
  };
  const { resultSet: LimitsResultSet, isLoading: LimitCubeIsLoading }: any =
    useTrackedCubeQuery(limitsCubeQuery, {
      skip: hasInvalidCubePayload(limitsCubeQuery),
    });

  const thresholdLimits = useMemo(() => {
    let LL = undefined;
    let UL = undefined;
    if (LimitsResultSet?.loadResponses?.[0]?.data?.length) {
      LimitsResultSet?.loadResponses?.[0]?.data?.forEach((item: any) => {
        if (
          item[
            `${appContextProps?.selectedPlantForCube}` +
              "ActionableFiveMinAgg" +
              ".description"
          ] === "Lower Limit"
        ) {
          LL = Number(
            item[
              `${appContextProps?.selectedPlantForCube}` +
                "ActionableFiveMinAgg" +
                ".lastValue"
            ]
          );
        }
        if (
          item[
            `${appContextProps?.selectedPlantForCube}` +
              "ActionableFiveMinAgg" +
              ".description"
          ] === "Upper Limit"
        ) {
          UL = Number(
            item[
              `${appContextProps?.selectedPlantForCube}` +
                "ActionableFiveMinAgg" +
                ".lastValue"
            ]
          )
        }
      });
      return { LL, UL };
    } else {
      return { LL, UL };
    }
  }, [LimitsResultSet?.loadResponses?.[0]?.data]);
  useEffect(() => {
    setExcelData(resultSet?.loadResponses?.[0]?.data);
  }, [resultSet]);

  if (progress) return <b>Progessing..</b>;
  if (isLoading || LimitCubeIsLoading) return <CircularProgress size={15} />;
  if (error) return <ErrorOutlineIcon fontSize="small" color="error" />;
  if (resultSet) {
    const range: any = {};
    // const categories: any = new Set()
    const categories: any = [
      ...getFullRangeCategories(dateRange, granularity),
    ].reverse();
    const dimensionValue =
      resultSet?.loadResponses[0]?.query.dimensions === undefined ||
      resultSet?.loadResponses[0]?.query.dimensions[0] === undefined
        ? " . "
        : resultSet?.loadResponses[0].query.dimensions[0];
    const dimensionValue2 =
      resultSet?.loadResponses[0].query.dimensions === undefined ||
      resultSet?.loadResponses[0].query.dimensions[1] === undefined
        ? " . "
        : resultSet?.loadResponses[0].query.dimensions[1];

    const measureValue =
      resultSet?.loadResponses[0].query.measures === undefined ||
      resultSet?.loadResponses[0].query.measures[0] === undefined
        ? " . "
        : resultSet?.loadResponses[0].query.measures[0];

    resultSet?.tablePivot().forEach((item: any) => {
      const getTimeStamp = getTimeStampKey(dimensionValue, resultSet);
      const getDescription = getDescriptionKey(dimensionValue);
      const getValue = getValueKey(measureValue);
      const getTag = getTagKey(dimensionValue2);
      // categories.add(item[getTimeStamp])
      if (!range[item[getDescription]]) {
        range[item[getDescription]] = {
          name: item[getDescription],
          tag: item[getTag],
          data: [],
          categories: [],
        };
      }
      range[item[getDescription]].data.push(parseFloat(item[getValue]));
      range[item[getDescription]].categories.push(item[getTimeStamp]);
      
    });

    //creating data for complete range
    const moddedRange = Object.values(range);
    // For each Parameter check the timeStamp
    moddedRange.forEach((obj: any) => {
      const newData: any = [];
      categories.forEach((timeStamp: any) => {
        const idx = obj.categories.indexOf(timeStamp);
        if (idx !== -1) {
          newData.push(obj.data[idx]);
        } else {
          // For missing timestamp add null to the data
          newData.push(null);
        }
      });
      result.push({
        name: obj.name,
        tag: obj.tag,
        data: newData,
        color: obj.color,
      });
    });

    if (range) {
      const dataForChart = { ...range[tag] };

      return (
        <>
          {result.length === 0 ? (
            <Typography
              sx={{
                height: 150,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#232a58",
                color: "#fff",
                fontSize: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <SubtitlesOffOutlinedIcon fontSize="large"  sx={{opacity: "60%", marginBottom:"16px"}}/>
                <Typography variant="body2">No Data</Typography>
              </Box>
            </Typography>
          ) : (
            <LineChart
              categories={[
                ...getFullRangeCategories(dateRange, granularity),
              ].reverse()}
              data={[
                ...result.map((ele: any, index: any) => {
                  return {
                    name: ele.tag,
                    data: ele.data,
                    type: "line",
                    color: index === 0 ? "#1aae53" : null,
                  };
                }),
                {
                  name: "Lower Limit",
                  data: [],
                  type: "line",
                  color: "#E06060",
                },
                {
                  name: "Upper Limit",
                  data: [],
                  type: "line",
                  color: "#E06060",
                },
              ]}
              isExtended={isExtended}
              getTag={dataForChart.tag}
              height={height}
              width={width}
              tag={tag}
              unit={unit}
              granularity={granularity}
              fetchedAnnotations={fetchedAnnotations}
              limits={thresholdLimits}
              dateRange={dateRange}
            />
          )}
        </>
      );
    }
  }
  return null;
};

export default memo(LineChartCubeResponse);
